/**
 * React Query Hook: Get Credits Balance
 *
 * Hook for fetching the current user's credit balance.
 * Endpoint: GET /api/credits/balance
 */

import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getCreditsBalance } from "@/api/endpoints/credits";
import type { GetCreditsBalanceResponse } from "@/api/types/credits";
import { queryKeys } from "@/lib/queryClient";

/**
 * Hook to get the current user's credit balance
 *
 * Features:
 * - Automatically cached for 1 minute (stale time)
 * - Refetches when component mounts if data is stale
 * - Shared across all components using this hook
 * - Request deduplication (multiple components = 1 API call)
 *
 * @returns React Query result with credit balance
 */

export function useGetCreditsBalance(
  config?: UseQueryOptions
): UseQueryResult<GetCreditsBalanceResponse, Error> {
  return useQuery({
    queryKey: queryKeys.credits.balance(),
    queryFn: getCreditsBalance,
    staleTime: 1000 * 60, // 1 minute - credits change frequently
    ...config,
  }) as UseQueryResult<GetCreditsBalanceResponse>;
}
